/**
 * Tenant entitlements API. Calls GET /api/v1/entitlements (same origin; tenant implied by host).
 * Returns a safe default on error so the app works when the endpoint is missing or fails.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v1';

const DEFAULT_RESPONSE = {
  plan: '',
  flags: [],
  limits: {},
  version: null,
};

function normalizeLimitEntry(raw) {
  if (raw && typeof raw === 'object') {
    const unlimited = Boolean(raw.unlimited);
    const valueRaw = raw.value;
    const value = valueRaw == null ? 0 : Number(valueRaw);
    return {
      value: Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0,
      unlimited,
    };
  }
  if (raw == null) {
    return { value: 0, unlimited: true };
  }
  const value = Number(raw);
  return {
    value: Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0,
    unlimited: false,
  };
}

function normalizeLimits(rawLimits) {
  if (!rawLimits || typeof rawLimits !== 'object' || Array.isArray(rawLimits)) {
    return {};
  }
  const out = {};
  Object.entries(rawLimits).forEach(([key, value]) => {
    if (!key) return;
    out[String(key)] = normalizeLimitEntry(value);
  });
  return out;
}

/**
 * Fetch tenant entitlements. No auth; tenant is determined by host (subdomain).
 * @returns {Promise<{ plan: string, flags: Array<{ name: string, params: object }>, limits: object, version: string|null }>}
 */
export async function fetchEntitlements() {
  const url = `${API_BASE_URL}${API_BASE_URL.endsWith('/') ? '' : '/'}entitlements`;
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return DEFAULT_RESPONSE;
    }
    const data = await response.json();
    if (!response.ok) {
      return DEFAULT_RESPONSE;
    }
    const flags = Array.isArray(data.flags) ? data.flags : [];
    return {
      plan: data.plan != null ? String(data.plan) : '',
      flags: flags.map((f) => ({
        name: f && typeof f.name === 'string' ? f.name : '',
        params: f && typeof f.params === 'object' && f.params !== null ? f.params : {},
      })),
      limits: normalizeLimits(data.limits),
      version: data.version != null ? String(data.version) : null,
    };
  } catch {
    return DEFAULT_RESPONSE;
  }
}

export { normalizeLimitEntry, normalizeLimits };
